/*
 * How each brand looks on what its clients see (emails, the invoice PDF, the pay and quote pages): the old Steward's colors
 * and white logos (steward.php steward_trade_presets, clientframe.php). accent is the top band and links, deep the darker
 * accent for small text, menu the footer band (the website footer's color), cta the one main button. The brand kit in
 * Settings, Business info, Brand (owner, 2026-10-30) changes any of them: its accent, secondary (the footer band) and call
 * to action colors, and its white logo (else its plain logo) instead of the built-in white one. The logo sits on the accent
 * color, so a white logo reads best.
 */
import { z } from "zod";
import type { Trade } from "./core.js";

export const clientLook = z.object({
  accent: z.string(),
  deep: z.string(),
  menu: z.string(),
  cta: z.string(),
  /** Black or white, whichever reads on cta. */
  onCta: z.string(),
  /** A white logo for the colored top band, or null (then the brand's name is written there). */
  logo: z.string().nullable(),
});
export type ClientLook = z.infer<typeof clientLook>;

type Preset = Omit<ClientLook, "onCta">;

export const TRADE_LOOKS: Record<Trade, Preset> = {
  cleaning: {
    accent: "#00929F",
    deep: "#00737D",
    menu: "#0B4F55",
    cta: "#FE5000",
    logo: "https://thecleanersusa.com/wp-content/uploads/2026/09/The-Cleaners-Logo-White-1024x349.png",
  },
  lawn: {
    accent: "#367C2B",
    deep: "#2A6322",
    menu: "#173F28",
    cta: "#FFDE00",
    logo: "https://thegroundsmen.com/wp-content/uploads/2026/09/The-GroundsMen-Logo-04-1024x297.png",
  },
  washers: {
    accent: "#005EB8",
    deep: "#004A93",
    menu: "#0B2E63",
    cta: "#FE5000",
    logo: "https://exteriorwashers.com/wp-content/uploads/2026/09/The-Washers-04-1-1024x349.png",
  },
  agency: {
    accent: "#0A0A0A",
    deep: "#111111",
    menu: "#0A0A0A",
    cta: "#22C55E",
    logo: "https://themarketers.agency/wp-content/uploads/2026/09/the-marketers-logo-white-1024x379.png",
  },
  hq: { accent: "#1D1D1F", deep: "#111111", menu: "#1D1D1F", cta: "#1D1D1F", logo: null },
};

/** Black or white, whichever reads better on a color (the old steward_color onCta). */
export function inkOn(hex: string): string {
  const m = /^#([0-9a-f]{6})$/i.exec(hex);
  const n = m ? parseInt(m[1]!, 16) : 0;
  const light = (0.299 * ((n >> 16) & 255) + 0.587 * ((n >> 8) & 255) + 0.114 * (n & 255)) / 255;
  return light > 0.6 ? "#1D1D1F" : "#FFFFFF";
}

/** A color made darker by a share (0.2 is 20 percent), for deep from a brand's own accent. */
function darker(hex: string, by: number): string {
  const n = parseInt(hex.slice(1), 16) || 0;
  const c = (shift: number) => Math.round(((n >> shift) & 255) * (1 - by));
  return `#${((c(16) << 16) | (c(8) << 8) | c(0)).toString(16).padStart(6, "0").toUpperCase()}`;
}

/** The brand kit's colors (companies.accent_color, secondary_color, cta_color); null keeps the trade's own. */
export interface KitColors {
  accent?: string | null;
  menu?: string | null;
  cta?: string | null;
}

/** The look for a company of this trade, with its uploaded logo and brand kit colors when it has them. */
export function lookFor(trade: string, ownLogo?: string | null, kit: KitColors = {}): ClientLook {
  const p = TRADE_LOOKS[(trade in TRADE_LOOKS ? trade : "hq") as Trade];
  const logo = ownLogo?.startsWith("https://") ? ownLogo : p.logo;
  const accent = kit.accent ?? p.accent;
  const cta = kit.cta ?? p.cta;
  return {
    accent,
    deep: kit.accent ? darker(kit.accent, 0.2) : p.deep,
    menu: kit.menu ?? p.menu,
    cta,
    logo,
    onCta: inkOn(cta),
  };
}

// The Steward mark -------------------------------------------------------------------------------------------------------

/**
 * The key's color on each trade's accent, so every brand's Steward has its own tab icon and no one gets lost between tabs
 * (the old Steward's steward_trade_presets mark; head office is black and white).
 */
export const STEWARD_MARK_KEY: Record<Trade, string> = {
  cleaning: "#FFFFFF",
  lawn: "#FFDE00",
  washers: "#FDD33F",
  agency: "#FFFFFF",
  hq: "#FFFFFF",
};

export const stewardMark = z.object({ bg: z.string(), key: z.string() });
export type StewardMark = z.infer<typeof stewardMark>;

/** The mark's colors for a trade: the brand kit's accent when it set one, else the trade's own. */
export function stewardMarkFor(trade: string, accent?: string | null): StewardMark {
  const t = (trade in TRADE_LOOKS ? trade : "hq") as Trade;
  return { bg: accent ?? TRADE_LOOKS[t].accent, key: STEWARD_MARK_KEY[t] };
}

/** The Steward mark (a key on a rounded square) as SVG, the old Steward's steward_logo_svg. */
export function stewardMarkSvg(m: StewardMark): string {
  return (
    `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 64 64"><rect width="64" height="64" rx="14" fill="${m.bg}"/>` +
    `<g transform="rotate(45 32 32)" fill="${m.key}"><path fill-rule="evenodd" d="M19 21a11 11 0 1 0 0 22a11 11 0 0 0 0-22zm0 6a5 5 0 1 1 0 10a5 5 0 0 1 0-10z"/>` +
    `<rect x="26" y="29" width="29" height="6" rx="1.5"/><rect x="41" y="34" width="5.5" height="9" rx="1.5"/><rect x="49.5" y="34" width="5.5" height="6.5" rx="1.5"/></g></svg>`
  );
}
